import React, { useMemo, useState } from 'react';
import { Panel } from 'rsuite';

// Validated categorical palette (dataviz reference, fixed slot order). Pie slices
// are all-pairs, so we cap colored slices and always ship a legend + values +
// hover so identity is never carried by hue alone. Overflow folds into "Other".
// Exported so a PDF of the same data can be drawn in the same colours - a
// chart that changes palette on its way to print is a different chart.
export const CATEGORICAL = ['#2a78d6', '#008300', '#e87ba4', '#eda100', '#1baf7a', '#eb6834', '#4a3aa7', '#e34948'];
const OTHER_COLOR = '#a9a8a2';
const SURFACE = '#ffffff';

const fmtNum = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const r = Math.round((n + Number.EPSILON) * 100) / 100;
  return String(r);
};

// Donut arc path. Angles in degrees, 0 at top, clockwise.
const arcPath = (cx, cy, R, r, start, end) => {
  const pt = (radius, deg) => {
    const a = ((deg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
  };
  const large = end - start > 180 ? 1 : 0;
  const [x0, y0] = pt(R, start);
  const [x1, y1] = pt(R, end);
  const [x2, y2] = pt(r, end);
  const [x3, y3] = pt(r, start);
  return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r} ${r} 0 ${large} 0 ${x3} ${y3} Z`;
};

// `valueDisplay` picks what the legend and the hub read out:
//   'value'   (default) the measured value, then its share - "12.5 h - 34%"
//   'percent'           the share alone, for a chart whose subject IS the split
// Opt-in, so every existing caller keeps the value-first reading.
export default function PiePanel({ title, data, labelKey, valueKey, valueSuffix = '', maxSlices = 8, renderTable, className, onSelect, forceMode, valueDisplay = 'value' }) {
  const percentMode = valueDisplay === 'percent';
  const [mode, setMode] = useState('chart');
  const [active, setActive] = useState(null);

  // `forceMode` (used during PDF capture) overrides the user's chart/table toggle.
  const effectiveMode = forceMode || mode;

  // A slice is selectable when onSelect is given and it isn't the folded "Other".
  const selectable = (label) => Boolean(onSelect) && !String(label).startsWith('Other (');
  const pick = (label) => { if (selectable(label)) onSelect(label); };

  const slices = useMemo(() => {
    const rows = (data || [])
      .map((d) => ({ label: String(d[labelKey] ?? '—'), value: Number(d[valueKey]) || 0 }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
    if (rows.length <= maxSlices) return rows.map((r, i) => ({ ...r, color: CATEGORICAL[i % CATEGORICAL.length] }));
    const head = rows.slice(0, maxSlices - 1).map((r, i) => ({ ...r, color: CATEGORICAL[i % CATEGORICAL.length] }));
    const rest = rows.slice(maxSlices - 1);
    head.push({ label: `Other (${rest.length})`, value: rest.reduce((s, r) => s + r.value, 0), color: OTHER_COLOR });
    return head;
  }, [data, labelKey, valueKey, maxSlices]);

  const total = slices.reduce((s, r) => s + r.value, 0);

  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const R = 82;
  const r = 50;

  let cursor = 0;
  const arcs = slices.map((s, i) => {
    const start = (cursor / total) * 360;
    cursor += s.value;
    const end = (cursor / total) * 360;
    return { ...s, index: i, start, end, pct: total > 0 ? (s.value / total) * 100 : 0 };
  });

  const activeSlice = active != null ? arcs[active] : null;

  return (
    <Panel
      bordered
      className={className}
      style={{ flex: '1 1 320px', minWidth: 300 }}
      header={(
        <div className="d-flex justify-content-between align-items-center">
          <span>{title}</span>
          <div className="bdc-segment" role="group" aria-label="View toggle">
            <button type="button" className={`bdc-btn bdc-btn--sm${effectiveMode === 'chart' ? ' bdc-btn--primary' : ''}`} onClick={() => setMode('chart')} title="Chart" aria-label="Chart" aria-pressed={effectiveMode === 'chart'}>
              <i className="bi bi-pie-chart" aria-hidden="true" />
            </button>
            <button type="button" className={`bdc-btn bdc-btn--sm${effectiveMode === 'table' ? ' bdc-btn--primary' : ''}`} onClick={() => setMode('table')} title="Table" aria-label="Table" aria-pressed={effectiveMode === 'table'}>
              <i className="bi bi-table" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    >
      {effectiveMode === 'table' ? (
        renderTable()
      ) : total === 0 ? (
        <div className="text-muted text-center py-4">No data.</div>
      ) : (
        <div className="d-flex flex-column align-items-center">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${title} pie chart`}>
            {arcs.length === 1 ? (
              <circle cx={cx} cy={cy} r={(R + r) / 2} fill="none" stroke={arcs[0].color} strokeWidth={R - r} />
            ) : (
              arcs.map((a) => (
                <path
                  key={a.label}
                  d={arcPath(cx, cy, R, r, a.start, a.end)}
                  fill={a.color}
                  stroke={SURFACE}
                  strokeWidth={2}
                  opacity={active == null || active === a.index ? 1 : 0.35}
                  style={{ transition: 'opacity 120ms', cursor: selectable(a.label) ? 'pointer' : 'default' }}
                  onMouseEnter={() => setActive(a.index)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => pick(a.label)}
                />
              ))
            )}
            <text x={cx} y={cy - 4} textAnchor="middle" style={{ fontSize: 20, fontWeight: 700, fill: 'var(--dm-text, #0b0b0b)' }}>
              {percentMode
                ? `${fmtNum(activeSlice ? activeSlice.pct : 100)}%`
                : `${fmtNum(activeSlice ? activeSlice.value : total)}${valueSuffix}`}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 11, fill: 'var(--dm-text-faint, #898781)' }}>
              {percentMode
                ? (activeSlice ? 'of shown' : 'total')
                : (activeSlice ? `${fmtNum(activeSlice.pct)}%` : 'total')}
            </text>
          </svg>

          <div className="w-100 mt-2" style={{ maxHeight: 168, overflowY: 'auto' }}>
            {arcs.map((a) => (
              <div
                key={a.label}
                className="d-flex align-items-center gap-2 py-1"
                style={{ cursor: selectable(a.label) ? 'pointer' : 'default', opacity: active == null || active === a.index ? 1 : 0.5 }}
                onMouseEnter={() => setActive(a.index)}
                onMouseLeave={() => setActive(null)}
                onClick={() => pick(a.label)}
                title={selectable(a.label) ? `Filter tables by ${a.label}` : a.label}
              >
                <span style={{ width: 12, height: 12, borderRadius: 3, background: a.color, flex: '0 0 auto' }} />
                <span className="text-truncate" style={{ flex: '1 1 auto', fontSize: 13 }} title={a.label}>{a.label}</span>
                <span className="text-muted" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                  {percentMode
                    ? `${fmtNum(a.pct)}%`
                    : `${fmtNum(a.value)}${valueSuffix} · ${fmtNum(a.pct)}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
